"use client"

import Typography from "../common/typography";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";

type LandingPageReviewcardProps = {
  reviewerName: string,
  images: Array<{ url: string | null, alt: string | null }>,
  usefulCount: number,
  reviewText: string
}

export function LandingPageReviewcard(props: LandingPageReviewcardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const reviewTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (reviewTextRef.current) {
        const element = reviewTextRef.current;
        // Compare scrollHeight with clientHeight to detect overflow
        setIsOverflowing(element.scrollHeight > element.clientHeight);
      }
    };

    // Check immediately
    checkOverflow();

    // Re-check on window resize
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [props.reviewText]);

  return (
    <div className="flex flex-col gap-6 p-30px overflow-hidden min-h-full justify-between bg-white shadow-lp rounded-3xl lg:w-[38.375rem] w-full ">
      <section>
        <div className="flex flex-row gap-3 h-fit">
          <div className="w-11 h-11 rounded-full flex justify-center items-center bg-dark-gray">
            <Typography color="white" weight={600}>{props.reviewerName.substring(0, 1)}</Typography>
          </div>
          <div className="flex flex-col h-fit">
            <div className="flex flex-row gap-1">
              {new Array(5).fill(0).map((item, idx) =>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" key={`review-start-${idx}`} color="#f29a05">
                  <g clipPath="url(#clip0_28_8746)">
                    <path d="M9.53834 1.60996C9.70914 1.19932 10.2909 1.19932 10.4617 1.60996L12.5278 6.57744C12.5998 6.75056 12.7626 6.86885 12.9495 6.88383L18.3123 7.31376C18.7556 7.3493 18.9354 7.90256 18.5976 8.19189L14.5117 11.6919C14.3693 11.8139 14.3071 12.0053 14.3506 12.1876L15.5989 17.4208C15.7021 17.8534 15.2315 18.1954 14.8519 17.9635L10.2606 15.1592C10.1006 15.0615 9.89938 15.0615 9.73937 15.1592L5.14806 17.9635C4.76851 18.1954 4.29788 17.8534 4.40108 17.4208L5.64939 12.1876C5.69289 12.0053 5.6307 11.8139 5.48831 11.6919L1.40241 8.19189C1.06464 7.90256 1.24441 7.3493 1.68773 7.31376L7.05054 6.88383C7.23744 6.86885 7.40024 6.75056 7.47225 6.57744L9.53834 1.60996Z" fill="currentColor"></path>
                  </g>
                </svg>
              )}
            </div>
            <Typography weight={500}>{props.reviewerName}</Typography>
          </div>
        </div>
        <div className="row-span-2 mt-6">
          <div
            ref={reviewTextRef}
            className={isExpanded ? "" : "line-clamp-4"}
          >
            <Typography>{props.reviewText}</Typography>
          </div>
          {isOverflowing && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm font-medium text-primary hover:underline mt-2 focus:outline-none"
              aria-expanded={isExpanded}
            >
              {isExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
        <div className="row-span-2 mt-6 grid grid-flow-col auto-cols-[5.1875rem] gap-2 overflow-x-scroll no-scrollbar">
          {
            props.images.filter(item => item.url).map((item, idx) =>
              <Image
                src={item.url!}
                alt={item.alt!}
                height={110}
                width={83}
                key={`reviewer-image-${idx}`}
                className="w-full h-full object-cover"
              />
            )
          }
        </div>
      </section>
      <Typography color="copyGray">
        {props.usefulCount} people found this useful
      </Typography>
    </div>
  )
}